import { useState, useEffect, useCallback, useRef } from 'react';
import { Message, MessageType } from '../models/Message';
import { PublicForm } from '../models/PublicForm';
import FirestoreRepository from '../services/FirestoreRepository';
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

const useChat = (form: PublicForm | undefined, userId: string | null) => {
  console.log('=== useChat: Hook initializing ===');
  console.log('useChat: Parameters:', {
    hasForm: !!form,
    formId: form?.id,
    userId
  });
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState<Record<string, any>>({});
  
  const repository = new FirestoreRepository();
  const introMessageCreatedRef = useRef(false);

  // Reset intro message created flag when form changes
  useEffect(() => {
    introMessageCreatedRef.current = false;
  }, [form?.id]);

  // Create intro message when conversation is empty
  const createIntroMessage = useCallback(async () => {
    if (!form?.introMessage || !form?.id || !userId || introMessageCreatedRef.current) {
      return;
    }
    
    introMessageCreatedRef.current = true;
    console.log('useChat: Creating intro message:', form.introMessage);
    
    try {
      const messageId = `intro_${uuidv4()}`;
      const introMessage: Message = {
        id: messageId,
        messageText: form.introMessage,
        fromId: form.id,
        toId: userId,
        channelUid: form.channelUid || '',
        timestamp: form.timestamp || Timestamp.now(),
        formQuestions: form.questions,
      };
      
      await repository.sendFormMessage(form.id, introMessage);
      console.log('useChat: Intro message sent successfully');
    } catch (error) {
      console.error('Error creating intro message:', error);
    }
  }, [form?.id, form?.introMessage, form?.channelUid, form?.questions, userId]);

  // Listen for messages
  useEffect(() => {
    console.log('useChat: Setting up message listener effect');
    console.log('useChat: Dependencies:', {
      formId: form?.id,
      userId,
      hasForm: !!form,
      hasUserId: !!userId
    });
    
    if (!form?.id || !userId) {
      console.log('⚠️ useChat: Missing required parameters, skipping listener setup');
      return;
    }
    
    console.log('🔗 useChat: Setting up Firestore listener for form:', form.id);
    setIsLoading(true);
    
    const unsubscribe = repository.listenForMessages(form.id, userId, (messagesList) => {
      console.log('useChat: Received messages update:', {
        count: messagesList.length,
        formId: form.id
      });
      
      if (messagesList.length > 0) {
        console.log('useChat: Latest messages:', messagesList.slice(-2).map(msg => ({
          id: msg.id,
          from: msg.fromId,
          preview: msg.messageText.substring(0, 50) + '...'
        })));
      }
      
      setMessages(messagesList);
      setIsLoading(false);
      
      // Check if we need to create an intro message
      const hasIntroMessage = messagesList.some(msg => msg.id.startsWith('intro_'));
      
      if (hasIntroMessage) {
        // If intro message exists, mark as created so we don't try again
        introMessageCreatedRef.current = true;
        console.log('useChat: Intro message already exists, skipping creation');
      } else if (form?.introMessage && messagesList.length === 0) {
        console.log('useChat: Empty conversation detected with intro message, creating intro message');
        createIntroMessage();
      }
    });
    
    return () => {
      console.log('useChat: Cleaning up message listener');
      unsubscribe();
    };
  }, [form?.id, userId, createIntroMessage]);

  // Parse form questions
  useEffect(() => {
    console.log('useChat: Parsing form questions');
    console.log('useChat: Form questions data:', {
      hasQuestions: !!form?.questions,
      questionsType: typeof form?.questions
    });
    
    if (!form?.questions) {
      console.log('⚠️ useChat: No questions data available');
      return;
    }
    
    const parsedQuestions = repository.parseQuestions(form.questions);
    console.log('useChat: Parsed questions:', {
      sectionCount: Object.keys(parsedQuestions).length,
      sections: Object.keys(parsedQuestions)
    });
    
    setQuestions(parsedQuestions);
  }, [form?.questions]);

  // Update new message text
  const updateNewMessage = useCallback((text: string) => {
    setNewMessage(text);
  }, []);

  // Send message with optional text override, and an optional image URL
  // (image-question chat attachments — spec §4a of the Image Questions
  // contract). When imageMessageUrl is set the message is tagged
  // MessageType.IMAGE so it decodes/renders as a photo bubble.
  const sendMessage = useCallback(async (messageTextOverride?: string, imageMessageUrl?: string) => {
    console.log('sendMessage called in useChat hook');

    // Use the override text if provided, otherwise use the state value
    const messageText = messageTextOverride || newMessage.trim();

    if (!messageText || !userId || !form?.id) {
      console.log('Cannot send message, missing required fields:', {
        hasMessage: !!messageText,
        hasUserId: !!userId,
        hasFormId: !!form?.id
      });
      return;
    }

    console.log('Sending message:', {
      formId: form.id,
      channelUid: form.channelUid,
      userId,
      message: messageText
    });

    setIsLoading(true);

    try {
      // Generate a UUID for the message ID
      const messageId = uuidv4();
      console.log('Generated UUID for message:', messageId);

      // Create message with ID included
      const message: Message = {
        id: messageId,
        messageText: messageText,
        fromId: userId,
        toId: form.id,
        channelUid: form.channelUid || '', // Ensure channelUid is never undefined
        timestamp: Timestamp.now(),
        formQuestions: form.questions,
        ...(imageMessageUrl
          ? { imageMessageUrl, messageType: MessageType.IMAGE }
          : {}),
      };

      console.log('Message object created with ID:', message.id);
      console.log('Full message object:', JSON.stringify(message));
      
      console.log('Calling repository.sendFormMessage...');
      await repository.sendFormMessage(form.id, message);
      console.log('Message sent successfully with ID:', messageId);
      
      // Only clear the input if we're using the state value
      if (!messageTextOverride) {
        setNewMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace available');
    } finally {
      setIsLoading(false);
    }
  }, [form?.id, form?.channelUid, form?.questions, newMessage, userId]);

  // Remove all messages
  const removeAllMessages = useCallback(async () => {
    if (!form?.id || !userId) return;
    
    setIsLoading(true);
    
    try {
      await repository.removeAllMessages(form.id, userId);
      // The listener will update the messages list
    } catch (error) {
      console.error('Error removing messages:', error);
    } finally {
      setIsLoading(false);
    }
  }, [form?.id, userId]);

  return {
    messages,
    newMessage,
    isLoading,
    questions,
    updateNewMessage,
    sendMessage,
    removeAllMessages,
  };
};

export default useChat;